import { Link, useParams } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { useSeo } from "../../lib/useSeo";
import { BY_SLUG, sqFt, UNIT_SIZES } from "./data/unitSizes";

/**
 * Storage unit sizes — an index and one page per size.
 *
 *   /storage-unit-sizes          — the comparison table
 *   /storage-unit-sizes/{slug}   — 5x5, 5x10, 10x10, 10x15, 10x20, 10x25,
 *                                  10x30, 20x20
 *
 * This is the axis that was missing. We had 2,346 submitted URLs, and 2,044 of
 * them were one of two templates multiplied by 51 states — an axis Keyword
 * Planner scores at ZERO searches a month. Meanwhile the size axis, which we
 * had no pages for, carries 3,600/mo on the category term and a real tail
 * underneath it (5×10 at 2,400, 10×10 at 1,900, 10×20 at 880, 10×15 at 720).
 *
 * The content is written to be worth the click on its own. Somebody comparing
 * unit sizes is usually about to rent one, not buy software, and that is fine:
 * it is exactly how the sibling product that works gets found. Casetop's
 * biggest entry point is "teeth numbers" at 49,500 a month, a query no lab
 * owner types while shopping for software, and it still turned into four times
 * our sign-ups. So the operator pitch here sits at the bottom, once, and does
 * not pretend the visitor came for it.
 */

function OperatorFooter() {
  return (
    <Card className="mt-10 p-6">
      <Pill>For operators</Pill>
      <h2 className="t-display-sm mt-3">Running the facility rather than renting the unit?</h2>
      <p className="text-sub mt-2 max-w-[640px]">
        NestPlot handles the unit mix, online move-ins, rent collection straight
        into your own Stripe account, and the lien timeline your state actually
        requires. Flat from $59 a month, and we take no percentage of your rent.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link to="/book-demo">
          <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
            Book a 15-minute walkthrough
          </Btn>
        </Link>
        <Link to="/for-storage">
          <Btn kind="ghost" size="md">
            What it does →
          </Btn>
        </Link>
      </div>
    </Card>
  );
}

export function UnitSizeIndexPage() {
  useSeo({
    title: "Storage Unit Sizes: Dimensions, What Fits, and Typical Price",
    description:
      "Every common self-storage unit size side by side — 5×5 through 20×20 — with dimensions, square footage, what genuinely fits inside, and the typical monthly price range.",
    path: "/storage-unit-sizes",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Self-storage unit sizes",
      itemListElement: UNIT_SIZES.map((u, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `${u.label} storage unit`,
        url: `https://nestplot.com/storage-unit-sizes/${u.slug}`,
      })),
    },
  });

  return (
    <div className="mx-auto max-w-[1040px] px-5 py-12">
      <Pill>Reference</Pill>
      <h1 className="t-display-xl mt-4">Storage unit sizes</h1>
      <p className="text-lg text-sub mt-4 max-w-[680px]">
        Every common size, what fits in it, and roughly what it costs a month.
        People reliably pick one size too small, so each page also says plainly
        what will not fit.
      </p>

      <div className="mt-8 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-sub">
              <th className="py-2 pr-4 font-medium">Size</th>
              <th className="py-2 pr-4 font-medium">Square feet</th>
              <th className="py-2 pr-4 font-medium">Roughly</th>
              <th className="py-2 pr-4 font-medium">Typical / month</th>
            </tr>
          </thead>
          <tbody>
            {UNIT_SIZES.map((u) => (
              <tr key={u.slug} className="border-t border-line">
                <td className="py-3 pr-4 font-medium">
                  <Link className="underline" to={`/storage-unit-sizes/${u.slug}`}>
                    {u.label}
                  </Link>
                </td>
                <td className="py-3 pr-4 tabular-nums">{sqFt(u)} sq ft</td>
                <td className="py-3 pr-4 text-sub">{u.comparison}</td>
                <td className="py-3 pr-4 tabular-nums">
                  ${u.priceLow}–{u.priceHigh}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-sub text-sm mt-4">
        Prices are typical United States asking rents and move a long way by
        metro. Want a recommendation from what you are actually storing? The{" "}
        <Link className="underline" to="/tools/unit-size-calculator">
          unit-size calculator
        </Link>{" "}
        takes a list and returns a size.
      </p>

      <OperatorFooter />
    </div>
  );
}

export function UnitSizePage() {
  const { size } = useParams<{ size: string }>();
  const u = size ? BY_SLUG[size] : undefined;

  useSeo({
    title: u
      ? `${u.label} Storage Unit: Size, What Fits, and Cost`
      : "Storage Unit Sizes",
    description: u
      ? `A ${u.label} storage unit is ${u.widthFt}×${u.depthFt} feet, ${sqFt(u)} square feet — ${u.comparison}. What fits inside, what does not, and the typical monthly price.`
      : "Self-storage unit sizes, what fits in each, and typical monthly price.",
    path: u ? `/storage-unit-sizes/${u.slug}` : "/storage-unit-sizes",
    jsonLd: u
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: `How big is a ${u.label} storage unit?`,
              acceptedAnswer: {
                "@type": "Answer",
                text: `A ${u.label} unit measures ${u.widthFt} feet by ${u.depthFt} feet, which is ${sqFt(u)} square feet — ${u.comparison}.`,
              },
            },
            {
              "@type": "Question",
              name: `What fits in a ${u.label} storage unit?`,
              acceptedAnswer: { "@type": "Answer", text: u.fits.join(". ") + "." },
            },
            {
              "@type": "Question",
              name: `How much does a ${u.label} storage unit cost?`,
              acceptedAnswer: {
                "@type": "Answer",
                text: `Typically $${u.priceLow} to $${u.priceHigh} a month in the United States, varying widely by metro and by whether the unit is climate controlled.`,
              },
            },
          ],
        }
      : undefined,
  });

  if (!u) {
    return (
      <div className="mx-auto max-w-[700px] px-5 py-16">
        <h1 className="t-display-lg">That size is not one we list</h1>
        <p className="text-sub mt-3">
          <Link className="underline" to="/storage-unit-sizes">
            See every storage unit size →
          </Link>
        </p>
      </div>
    );
  }

  const others = UNIT_SIZES.filter((o) => o.slug !== u.slug);

  return (
    <div className="mx-auto max-w-[840px] px-5 py-12">
      <p className="text-sm text-sub">
        <Link className="underline" to="/storage-unit-sizes">
          Storage unit sizes
        </Link>{" "}
        / {u.label}
      </p>

      <h1 className="t-display-xl mt-4">{u.label} storage unit</h1>
      <p className="text-lg text-sub mt-4">
        {u.widthFt} feet by {u.depthFt} feet, so {sqFt(u)} square feet —{" "}
        {u.comparison}. Typically ${u.priceLow} to ${u.priceHigh} a month.
      </p>

      <Card className="mt-8 p-6">
        <h2 className="t-display-sm">What fits</h2>
        <ul className="mt-3 space-y-2">
          {u.fits.map((f) => (
            <li key={f} className="flex gap-2.5">
              <span className="text-accent mt-[2px]">
                <Icon name="check" size={14} />
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <p className="text-sub mt-5">
          <strong>What does not:</strong> {u.doesNotFit}.
        </p>
      </Card>

      <Card className="mt-6 p-6">
        <h2 className="t-display-sm">If this is the wrong size</h2>
        <p className="text-sub mt-2">
          Going one size up costs less than renting a second unit later, and
          people under-estimate more often than they over-estimate.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {others.map((o) => (
            <Link key={o.slug} to={`/storage-unit-sizes/${o.slug}`}>
              <Pill>{o.label}</Pill>
            </Link>
          ))}
        </div>
        <p className="text-sub text-sm mt-4">
          Or list what you are storing and let the{" "}
          <Link className="underline" to="/tools/unit-size-calculator">
            unit-size calculator
          </Link>{" "}
          pick for you.
        </p>
      </Card>

      <OperatorFooter />
    </div>
  );
}
